export interface DeviceInfo {
  app_version?: string;
  platform?: string;
  os_version?: string;
  browser?: string;
  browser_version?: string;
  is_mobile?: boolean;
  is_webview?: boolean;
  device_make?: string;
  device_model?: string;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  connection_type?: string;
  screen_width?: number;
  screen_height?: number;
  viewport_width?: number;
  viewport_height?: number;
  device_pixel_ratio?: number;
  language?: string;
  timezone?: string;
}

// Hoisted to module scope so they're compiled once rather than on every call.
// collectDeviceInfo() runs once per init today, so this is a nice-to-have, not a hot path.
const IOS_PLATFORM_RE = /(?:iPhone|iPad|iPod).*OS (\d+)[_.](\d+)/;
const ANDROID_PLATFORM_RE = /Android (\d+(?:\.\d+)*)/;
const WINDOWS_NT_RE = /Windows NT (\d+\.\d+)/;
const MAC_OS_RE = /Mac OS X (\d+[._]\d+(?:[._]\d+)?)/;
const MAC_OS_SEP_RE = /_/g;
const IPHONE_IPAD_RE = /iPhone|iPad/;
const SAFARI_VERSION_RE = /Version\/\d+.*Safari/;
const SAFARI_BROWSER_RE = /Version\/(\d+).*Safari/;
const CHROME_RE = /Chrome\/(\d+)/;
const APPLE_DEVICE_RE = /iPhone|iPad|iPod/;
const SAMSUNG_BROWSER_RE = /SamsungBrowser/;
const IPAD_RE = /iPad/;
const BROWSER_CHECKS: [RegExp, string][] = [
  [/SamsungBrowser\/(\d+)/, 'Samsung Browser'],
  [/Edg\/(\d+)/, 'Edge'],
  [/Firefox\/(\d+)/, 'Firefox'],
  [/OPR\/(\d+)/, 'Opera'],
];

function detectIsWebView(ua: string, hasAppVersion: boolean): boolean {
  if (ua.includes('; wv)')) return true; // Android WebView marker
  // iOS WKWebView: iPhone/iPad UA without a standalone Safari version line
  if (IPHONE_IPAD_RE.test(ua) && !ua.includes('CriOS') && !ua.includes('FxiOS') && !SAFARI_VERSION_RE.test(ua)) return true;
  return hasAppVersion; // native apps always pass app_version
}

function parsePlatformFromUA(ua: string): { platform?: string; os_version?: string; is_mobile: boolean } {
  const iosMatch = ua.match(IOS_PLATFORM_RE);
  if (iosMatch) {
    return { platform: 'iOS', os_version: `${iosMatch[1]}.${iosMatch[2]}`, is_mobile: true };
  }

  const androidMatch = ua.match(ANDROID_PLATFORM_RE);
  if (androidMatch) {
    return { platform: 'Android', os_version: androidMatch[1], is_mobile: true };
  }

  if (ua.includes('Windows')) {
    const m = ua.match(WINDOWS_NT_RE);
    const versions: Record<string, string> = { '10.0': '10/11', '6.3': '8.1', '6.2': '8', '6.1': '7' };
    return { platform: 'Windows', os_version: m ? (versions[m[1]] ?? m[1]) : undefined, is_mobile: false };
  }

  if (ua.includes('Mac OS X')) {
    const m = ua.match(MAC_OS_RE);
    return { platform: 'macOS', os_version: m ? m[1].replace(MAC_OS_SEP_RE, '.') : undefined, is_mobile: false };
  }

  if (ua.includes('CrOS')) return { platform: 'ChromeOS', is_mobile: false };
  if (ua.includes('Linux')) return { platform: 'Linux', is_mobile: false };

  return { is_mobile: false };
}

function parseBrowserFromUA(ua: string): { browser?: string; browser_version?: string } {
  // Order matters: more specific patterns first
  for (const [re, name] of BROWSER_CHECKS) {
    const m = ua.match(re);
    if (m) return { browser: name, browser_version: m[1] };
  }

  // Chrome must come before Safari (Chrome UA also contains "Safari")
  const chromeMatch = ua.match(CHROME_RE);
  if (chromeMatch) return { browser: 'Chrome', browser_version: chromeMatch[1] };

  const safariMatch = ua.match(SAFARI_BROWSER_RE);
  if (safariMatch) return { browser: 'Safari', browser_version: safariMatch[1] };

  return {};
}

function detectDeviceMake(ua: string, model?: string): string | undefined {
  if (APPLE_DEVICE_RE.test(ua)) return 'Apple';
  if (SAMSUNG_BROWSER_RE.test(ua)) return 'Samsung';
  if (model) {
    if (model.startsWith('SM-')) return 'Samsung';
    if (model.startsWith('Pixel')) return 'Google';
  }
  return undefined;
}

function detectDeviceType(ua: string, isMobile: boolean): 'mobile' | 'tablet' | 'desktop' {
  if (IPAD_RE.test(ua)) return 'tablet';
  if (isMobile) return 'mobile';
  return 'desktop';
}

export async function collectDeviceInfo(): Promise<DeviceInfo> {
  if (typeof navigator === 'undefined') return {};

  const ua = navigator.userAgent;
  const { platform, os_version, is_mobile } = parsePlatformFromUA(ua);
  const { browser, browser_version } = parseBrowserFromUA(ua);

  let device_model: string | undefined;
  let refined_os_version = os_version;

  // Use Client Hints API for precise platform version and device model (Chromium only)
  const uaData = (navigator as any).userAgentData;
  if (uaData?.getHighEntropyValues) {
    try {
      const hints = await uaData.getHighEntropyValues(['platformVersion', 'model']);
      if (hints.platformVersion) refined_os_version = hints.platformVersion;
      if (hints.model) device_model = hints.model;
    } catch {
      // Client Hints unavailable or denied — UA parse results stand
    }
  }

  const rc = typeof window !== 'undefined' ? (window as any).__RUNTIME_CONFIG__ : undefined;
  const appVersion: string | undefined = rc?.appVersion || undefined;
  const connection = (navigator as any).connection;

  return {
    app_version: appVersion,
    platform,
    os_version: refined_os_version,
    browser,
    browser_version,
    is_mobile,
    is_webview: detectIsWebView(ua, !!appVersion),
    device_make: detectDeviceMake(ua, device_model),
    device_model: device_model || undefined,
    device_type: detectDeviceType(ua, !!is_mobile),
    connection_type: connection?.effectiveType || undefined,
    screen_width: typeof screen !== 'undefined' ? screen.width : undefined,
    screen_height: typeof screen !== 'undefined' ? screen.height : undefined,
    viewport_width: typeof window !== 'undefined' ? window.innerWidth : undefined,
    viewport_height: typeof window !== 'undefined' ? window.innerHeight : undefined,
    device_pixel_ratio: typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
    language: navigator.language || undefined,
    timezone: Intl?.DateTimeFormat().resolvedOptions().timeZone || undefined,
  };
}
